/**
 *
 * EditUsernameLayout
 *
 */

import React, { useEffect, useMemo, useState } from "react";
import classnames from "classnames";
import { Button, stylize } from "@envestnet/envreact-component-library";
import UpdateUsername from "components/UpdateUsername";
import { UsernameUniqueErrorType } from "components/UpdateUsername/types";
import { UserInfo } from "types/UserInfo";
import { validateUsername } from "utils/usernameValidator";
import withIntl from "utils/withIntl";

import messages from "./messages";
import styles, { Styles } from "./styles";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;

  userInfo: UserInfo;
  userName: string;
  usernameUpdateErrorType: UsernameUniqueErrorType;
  submittingUsernameUpdate: boolean;
  clearUsernameUpdateError: () => void;
  submitUsernameUpdate: (payload: { username: string }) => void;
  goToSecuritySettings: () => void;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below
}

export interface EditUsernameLayoutProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;
}

const defaultProps: DefaultProps = {};

type Props = InjectedProps & EditUsernameLayoutProps;

const toValidationErrorMessage = (
  errorCode: ReturnType<typeof validateUsername>["errorCode"],
  formatMessage: FormatMessage
): string | null => {
  if (errorCode === "REQUIRED") {
    return formatMessage(messages.validationError);
  }

  if (errorCode === "UNCHANGED") {
    return null;
  }

  if (errorCode === "FORMAT") {
    return formatMessage(messages.validationError);
  }

  return null;
};

const EditUsernameLayout = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    userName,
    usernameUpdateErrorType,
    submittingUsernameUpdate,
    clearUsernameUpdateError,
    submitUsernameUpdate,
    goToSecuritySettings,
  } = props;

  const [username, setUsername] = useState<string>(userName || "");
  const [uiValidationError, setUiValidationError] = useState<string | null>(
    null
  );

  useEffect(() => {
    setUsername(userName || "");
    setUiValidationError(null);
    clearUsernameUpdateError();
  }, [userName]);

  const onUsernameChange = (value: string) => {
    setUsername(value);
    const result = validateUsername(value, userName);
    setUiValidationError(
      toValidationErrorMessage(result.errorCode, formatMessage)
    );
    clearUsernameUpdateError();
  };

  const uniqueValidationError = useMemo(() => {
    if (usernameUpdateErrorType === "DUPLICATE") {
      return formatMessage(messages.duplicateError);
    }

    return null;
  }, [usernameUpdateErrorType]);

  const usernameErrorMessage = uiValidationError || uniqueValidationError;

  const allowSave = useMemo(() => {
    if (submittingUsernameUpdate || uiValidationError) {
      return false;
    }

    return validateUsername(username, userName).isValid;
  }, [username, uiValidationError, submittingUsernameUpdate, userName]);

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div className={classes.content}>
        <header className={classnames(classes.header, "header-font-family")}>
          {formatMessage(messages.updateUsername)}
        </header>

        <UpdateUsername
          username={username}
          flow="DASHBOARD"
          onUsernameChange={onUsernameChange}
          errorMessage={usernameErrorMessage}
        />

        <div className={classes.buttonContainer}>
          <Button
            buttonStyle="secondary"
            name={formatMessage(messages.cancel)}
            onClick={goToSecuritySettings}
            className={classnames(classes.btn, "header-font-family")}
            data-testid="cancel-button"
          />
          <Button
            buttonStyle="primary"
            disabled={!allowSave}
            loading={submittingUsernameUpdate}
            name={formatMessage(messages.save)}
            onClick={() => submitUsernameUpdate({ username: username.trim() })}
            className={classnames(classes.btn, "header-font-family")}
            data-testid="save-button"
          />
        </div>
      </div>
    </div>
  );
};

EditUsernameLayout.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(EditUsernameLayout)
);
